<template>
  <div :class="[embeddedMode ? '' : 'mx-6 my-4']">
    <div class="md:flex md:items-center md:justify-between md:space-x-5">
      <div v-if="!embeddedMode" class="flex items-center space-x-5">
        <div class="flex-shrink-0">
          <div class="relative bg-emerald-600/70 rounded-xl p-1">
            <mdicon name="arrow-right-bold-circle-outline" size="62" class="text-emerald-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="">
          <h1 class="mb-1 text-2xl font-bold text-gray-900">Handoff Reasons</h1>
          <p class="text-sm font-medium text-gray-500">Why Bots escalated conversations?</p>
        </div>
      </div>
      <div v-else class="flex items-center space-x-3 mb-1">
        <div class="flex-shrink-0">
          <div class="relative bg-violet-600/70 rounded-md p-1">
            <mdicon name="finance" size="30" class="text-violet-100" aria-hidden="true"></mdicon>
          </div>
        </div>
        <div class="">
          <h1 class="mb-1 text-xl font-semibold text-gray-800">Bot Dashboard</h1>
        </div>
      </div>
      <div class="flex flex-col-reverse justify-stretch space-y-4 space-y-reverse sm:flex-row-reverse sm:justify-end sm:space-x-3 sm:space-y-0 sm:space-x-reverse md:mt-0 md:flex-row md:space-x-3">
        <ProgressButton ref="analyzeProgressButton" title="Refresh" icon="refresh" @click="loadData"></ProgressButton>
        <button
          type="button"
          class="flex items-center gap-x-1 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
          @click="onExport"
        >
          <mdicon name="download-outline" class="h-5 w-5 mr-1" aria-hidden="true"></mdicon>
          Export
        </button>
        <button
          v-tooltip.bottom="'Data Assistant'"
          @click="onToggleDA"
          :class="[
            'px-2 mr-2 rounded border border-transparent shadow-gradient-glow-green',
            dataAssistantStore.visible ? 'text-slate-100 bg-emerald-600/70 hover:bg-emerald-500/70' : 'text-slate-600 hover:bg-slate-100/70',
          ]"
        >
          <mdicon name="creation" size="22" aria-hidden="true"></mdicon>
        </button>
      </div>
    </div>

    <FiltersList class="mt-2" @select-filter="onSelectFilter" @show-filters="showFilters"></FiltersList>

    <div class="overflow-hidden mt-6">
      <div class="flex items-center border-b border-gray-300 py-2 mb-4">
        <mdicon name="text" size="18" class="rounded-md bg-emerald-600/70 p-1 mr-2 text-white" aria-hidden="true"></mdicon>
        <h2 class="text-base font-semibold leading-7 text-gray-900">Most Frequent Handoff Reasons ( {{ reasonsList.length === 100 ? 'top' : '' }} {{ reasonsList.length }} )</h2>
      </div>
      <DataTable :value="reasonsList" sortField="count" :sortOrder="-1" paginator :rows="25" :rowsPerPageOptions="[25, 50, 100]" removableSort tableStyle="min-width: 300px;" class="">
        <!--<Column selectionMode="multiple" headerStyle="width: 1%"></Column>-->
        <Column field="reason" header="Reason" sortable style="width: 80%">
          <template #body="slotProps">
            <router-link :to="{ path: '/bots/conversations', query: { filter: getFilterQueryConversations(slotProps.data) } }" class="flex items-center hover:underline">
              <div class="cursor-pointer font-medium">{{ slotProps.data?.reason }}</div>
              <mdicon name="open-in-new" size="18" class="ml-1 text-slate-300" aria-hidden="true"></mdicon>
            </router-link>
          </template>
        </Column>
        <Column field="count" header="count" sortable />
      </DataTable>
    </div>

    <EditFiltersSidebar v-model:visible="filtersOpen"></EditFiltersSidebar>
  </div>
</template>

<script setup>
  import { onMounted, onBeforeUnmount, ref, reactive, computed, watch } from 'vue';
  import { useRoute, useRouter } from 'vue-router';
  import { useLayoutStore } from '@/store/LayoutStore';
  import { useBotConversationSearchStore } from '@/store/BotConversationSearchStore';
  import { useFiltersStore } from '@/store/FiltersStore';
  import { useDataAssistantStore } from '@/store/DataAssistantStore';
  import { useRbacStore } from '@/store/RBACStore';
  import { stdBotConversationFilters } from '@/analytics/filtersDefs';
  import { ArrowDownCircleIcon, HashtagIcon } from '@heroicons/vue/20/solid';
  import TimeRangeSelector from '@/components/TimeRange/TimeRangeSelector.vue';
  import dayjs from 'dayjs';
  import DbEasyPie from '@/components/Charts/DbEasyPie.vue';
  import ProgressButton from '@/components/Buttons/ProgressButton.vue';
  import { useDateRangeStore } from '@/store/DaterangeStore';
  import DbUplot from '@/components/Charts/DbUplot.vue';
  import uPlot from 'uplot';
  import EditFiltersSidebar from '@/components/Filters/EditFiltersSidebar.vue';
  import FiltersList from '@/components/Filters/FiltersList.vue';
  import dbColors from '@/components/Charts/dbcolors';
  import { observable10Colors, tableauColors } from '@/components/Charts/dbcolors';
  import { formatSeconds } from '@/utils/formatSeconds';
  import DataTable from 'primevue/datatable';
  import Column from 'primevue/column';
  import Button from 'primevue/button';
  import DAConversation from '@/components/DataAssistant/DAConversation.vue';
  import { utils, writeFileXLSX } from 'xlsx';

  const route = useRoute();
  const router = useRouter();
  const layout = useLayoutStore();
  const conversationSearchStore = useBotConversationSearchStore();
  const dateRangeStore = useDateRangeStore();
  const filtersStore = useFiltersStore();
  const rbac = useRbacStore();
  const dataAssistantStore = useDataAssistantStore();

  const loading = ref(false);
  const reasonsList = ref([]);
  // ??? below
  const conversationsTotal = ref(0);
  const conversationsContained = ref(0);
  const conversationsEscalated = ref(0);
  const conversationsAbandoned = ref(0);
  const botMessagesTotal = ref(0);
  const avgTTA = ref(0);
  const maxTTA = ref(0);
  const cSat = ref(0);
  const cSatMin = ref(0);

  // Data assistant
  const daMessages = ref([]);

  const embeddedMode = ref(route.meta?.embeddedMode === 'true');
  const analyzeProgressButton = ref(null);
  const filtersOpen = ref(false);

  const dataTitle = 'Handoff reasons';

  const dataDescription =
    'Data contains customer service chatbot handoff reasons.\n' +
    'Handoff happens when chatbot escalates conversation to a human, and reason describes why.\n' +
    'Each data record contains a reason (field "reason"), and how many times handoff with this reason occurred (field "count").\n' +
    'Some reasons may be worded differently but mean the same thing.\n' +
    'In your analysis always account for that, and generalize your insights accounting for that.\n';

  const suggestedQuestions = [
    'What insights can you derive from this data?',
    'What are the most frequent handoff reasons?',
    'What information is missing the most so Bot had to handoff?',
    'What can be done to reduce handoffs ?',
  ];

  watch(filtersStore.allFilters, (newValue) => {
    if (loading.value) {
      return;
    }
    console.log(`Filters changed: ${JSON.stringify(newValue)}`);
    loadData();
  });

  watch(
    () => dateRangeStore.timeIntervalChanged,
    (newValue) => {
      console.log(`Date range changed: ${JSON.stringify(newValue)}`);
      loadData();
    },
  );

  // todo reconsider - may want to enable to plot trend over time
  /*
  const uPlotCSatData = ref([]);
  const uPlotOptionsCSat = {
    scales: {
      x: { time: true },
      y: { range: { min: { soft: 0, mode: 2 }, max: { soft: 100, mode: 2 } } },
    },
    series: [
      {},
      {
        label: 'CSat',
        value: (u, v, sidx, didx) => (didx == null ? null : v == null ? '' : v.toFixed(1) + '%'),
        stroke: dbColors.hex2RGBA(observable10Colors[0], 0.7), //'rgba(78, 121, 167, 0.1 )',
        fill: dbColors.hex2RGBA(observable10Colors[0], 0.3), //'rgba(78, 121, 167, 0.4)',
      },
    ],
    axes: [
      {},
      {
        values: (u, vals, space) => vals.map((v) => +v.toFixed(1) + '%'),
      },
    ],
  };*/

  async function loadData() {
    analyzeProgressButton.value.startProgress();
    loading.value = true;

    const groupings = [
      // todo reconsider - may want to enable to plot trend over time
      /*
      {
        name: 'timeline',
        title: 'Date',
        type: 'date_histogram',
        interval: dateRangeStore.timeInterval.interval,
        extended_bounds: { min: dateRangeStore.timeInterval.from, max: dateRangeStore.timeInterval.to },
        groupings: [
          { name: 'escalated', type: 'filter', query: { term: { 'state.escalated': true } }, groupings: [{ name: 'reasons', title: 'Reasons', type: 'terms', field: 'state.handoffReason', size: 3 }] },
        ],
      },
      */
      { name: 'escalated', type: 'filter', query: { term: { 'state.escalated': true } }, groupings: [{ name: 'reasons', title: 'Reasons', type: 'terms', field: 'state.handoffReason', size: 100 }] },
    ];
    const aggregateResult = await conversationSearchStore.aggregate(groupings);
    processAggregateResult(aggregateResult);
    loading.value = false;
    analyzeProgressButton.value.stopProgress();
  }

  function processAggregateResult(aggregateResult) {
    if (!aggregateResult.success) {
      reasonsList.value = [];
      console.log(`Error aggregating data: ${aggregateResult.error}`);
      return;
    }

    let reasonsData = aggregateResult?.result?.aggregations?.escalated?.reasons?.buckets || [];
    if (!Array.isArray(reasonsData)) {
      reasonsData = [];
    }

    reasonsList.value = reasonsData.map((x) => {
      return {
        reason: x.key,
        count: x.doc_count,
      };
    });

    dataAssistantStore.setData(reasonsList.value);
  }

  function handleDbEvent(event) {
    switch (event.type) {
      case 'zoom': {
        console.log(`Handle Zoom`, event);
        let from = event?.minDate || null;
        let to = event?.maxDate || null;
        if (from && to) {
          dateRangeStore.setDateRange(from, to);
          loadData();
        }
        break;
      }
    }
  }

  function onSelectFilter(filter) {
    // TODO pass which filter to make active
    showFilters();
  }

  function showFilters() {
    filtersOpen.value = true;
  }

  function getFilterQueryConversations(data) {
    return [encodeURIComponent(`state.handoffReason:${data.reason}`)];
  }

  function onExport() {
    // reasons already sorted as in the table ???
    const reasonsData = reasonsList.value.map((r) => {
      return {
        Reason: r.reason,
        Count: r.count,
      };
    });

    const wsReasons = utils.json_to_sheet(reasonsData);
    const wb = utils.book_new();
    utils.book_append_sheet(wb, wsReasons, 'Reasons');
    writeFileXLSX(wb, 'HandoffReasons.xlsx');
  }

  function onToggleDA() {
    if (dataAssistantStore.visible) {
      dataAssistantStore.hide();
    } else {
      dataAssistantStore.show();
    }
  }

  /*
  watch(
    () => rbac.canUseDataAssistant,
    (newValue) => {
      if (newValue) {
        dataAssistantStore.show();
      } else {
        dataAssistantStore.hide();
      }
    },
  );
  */

  onMounted(() => {
    filtersStore.initFilters('botconv', stdBotConversationFilters);
    // clear filter on reason, if set, so we can see all reasons
    filtersStore.clearFilterValue('state.handoffReason');
    dataAssistantStore.init({
      assistantDataTitle: dataTitle,
      assistantDataDescription: dataDescription,
      assistantSuggestedQuestions: suggestedQuestions,
      show: rbac.canUseDataAssistant,
    });
    dataAssistantStore.show();
    loadData();
  });

  onBeforeUnmount(() => {
    // todo
    dataAssistantStore.hide();
  });
</script>
